'use client';

import { forwardRef, useState } from 'react';
import { cn } from '../../utils/cn';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'interactive' | 'glass';
  isHoverable?: boolean;
  spotlight?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    { children, variant = 'default', isHoverable = false, spotlight = false, className, ...props },
    ref
  ) => {
    const baseStyles = 'bg-white rounded-[2rem] border border-zinc-200/50';
    
    const variantStyles = {
      default: 'shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)]',
      elevated: 'shadow-[0_25px_50px_-12px_rgba(0,0,0,0.08)]',
      interactive: 'shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)] transition-all duration-300 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.08)] hover:border-zinc-300/50 active:scale-[0.995]',
      glass: 'bg-white/80 backdrop-blur-xl border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.2)]',
    };

    const [position, setPosition] = useState({ x: 50, y: 50 });

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!spotlight) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setPosition({ x, y });
    };

    return (
      <div
        ref={ref}
        onMouseMove={handleMouseMove}
        className={cn(
          baseStyles,
          variantStyles[variant],
          spotlight && 'spotlight-border relative',
          className
        )}
        {...props}
      >
        {spotlight && (
          <div 
            className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-[inherit]"
            style={{
              background: `radial-gradient(400px circle at ${position.x}% ${position.y}%, rgba(16, 185, 129, 0.1), transparent 40%)`,
            }}
          />
        )}
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

// Card Header
export const CardHeader = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-8 pt-8 pb-4', className)} {...props} />
  )
);
CardHeader.displayName = 'CardHeader';

// Card Title
export const CardTitle = forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn('text-lg font-semibold tracking-tight text-zinc-900', className)} {...props} />
  )
);
CardTitle.displayName = 'CardTitle';

// Card Description
export const CardDescription = forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('text-sm text-zinc-500 mt-1', className)} {...props} />
  )
);
CardDescription.displayName = 'CardDescription';

// Card Content
export const CardContent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-8 pb-8', className)} {...props} />
  )
);
CardContent.displayName = 'CardContent';

// Card Footer
export const CardFooter = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-8 pb-8 pt-4 flex items-center gap-3', className)} {...props} />
  )
);
CardFooter.displayName = 'CardFooter';
